import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { WorkflowService as CloudWorkflowService, Config, Odata, Utils, Account, Token } from '@mitel/cloudlink-sdk';
import { IntegrationConnectionPostRequest, ActivityPostRequest, ActivityPatchRequest, Activity, IntegrationConnectionPutRequest, WorkerPostRequest, WorkerPostResponse, IntegrationConnectionCollection, Tag, TagCollection, ActivityPatchOperation } from '@mitel/cloudlink-sdk/workflow';
import { Statement, PolicyPostRequest, Client } from '@mitel/cloudlink-sdk/admin';
import { AdminService } from './admin.service';
import { Observable, Subject, from, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { MSTeamsWorkflows, PresenceMonitorState } from '../interfaces/msteams-presence-data';
import { CloudLinkNotifyService } from './cloudlink-notify.service';
import { concatMap, map } from 'rxjs/operators';
import { Util } from '../utils/Util';

const logContext = 'WorkflowService';

const DisableAllButUsersStatement: Statement =  {
    "statementId": "deny-most-actions",
    "action": [
        "PUT",
        "POST",
        "DELETE",
        "PATCH",
        "HEAD"
    ],
    "resource": [
        "admin.+api\\.mitel\\.io/[0-9-]+/partners/.+",
        "billing.+api\\.mitel\\.io/[0-9-]+/partner-subscriptions",
        "billing.+api\\.mitel\\.io/[0-9-]+/licenses",
        "billing.+api\\.mitel\\.io/[0-9-]+/accounts",
        "director.+api\\.mitel\\.io/[0-9-]+/identities",
        "director.+api\\.mitel\\.io/[0-9-]+/services"
    ],
    "effect": "Deny"
};



@Injectable({providedIn: 'root'})

export class WorkflowService 
{
	private readonly api: CloudWorkflowService;
    public readonly cloud: string;
    // only keeping one workflowId and 1 workerId for now because
    // we only have one workflow that we can execute and we will always want the 
    // latest data from it
    // in the future if we have more than one workflow to execute
    // we will keep an array of workflows and perhaps keep
    // the notify subscription open full time
    private workerId: string = '';
    private workflowId: string = '';
    private notifyContent: any = {};

    // emit the workerId and the raw data returned for client to process
    workflowExecuteCompleted$: Subject<any> = new Subject();
    
    constructor( private adminService: AdminService, 
        private httpClient: HttpClient, 
        private notifyService: CloudLinkNotifyService,
        private authService: AuthService ) {
		this.cloud = Config.cloud = environment.cloud;
		this.api = new CloudWorkflowService();

        this.notifyService.eventNotification$.pipe(
            concatMap(message => {
                console.log(`${logContext}.eventNotification$ : workerId: ${this.workerId}, message=`, message);
                if ( message.topic != 'platform-api-workflow' ) // this is a general notification and we only care about WF
                    return undefined;
                this.notifyContent = message.content;
                if (this.workerId && this.notifyContent.workerId === this.workerId) {
                    return this.getWorkerByActivityIdAndWorkerId();
                } else {
                    console.log(`${logContext}.eventNotification$ : Worker ids DO NOT MATCH `);
                  return of([]);
                }
            })
        )
        .subscribe(async executeContent => {
            console.log(`${logContext}.eventNotification$ : content after get `, executeContent);
            if (executeContent && this.workerId && this.notifyContent?.workerId === this.workerId) {
              console.log(`${logContext}.eventNotification$ : workerId: ${this.workerId}, executeContent=`, executeContent);
              let resultData = {
                workerId: this.workerId,
                data: executeContent
              }
              this.workflowExecuteCompleted$.next(resultData);
              this.notifyService.unsubscribeFromWorkflow();
              this.workerId = '';
              this.workflowId = '';
              this.notifyContent = {};
            } else {
              console.log(`${logContext}  eventNotification$ ignored { workerId: ${this.notifyContent.workerId} }`);
            }
          });
	}

    getWorkerByActivityIdAndWorkerId() {
        const params = { activityId: this.workflowId, workerId: this.workerId };

        return from(this.api.getWorkerByActivityIdAndWorkerId(params)).pipe(
            map(result => {
                console.log(`${logContext}.getWorkerByActivityIdAndWorkerId() result=`, result);
    
                const outputs = (result as any).outputs;
                if ( Array.isArray(outputs) ) 
                {
                    return outputs;
                }
                else
                {
                    return {};
                }
            })
        );
    }

    get url(): string {
        return `https://workflow${
          this.cloud === 'dev' ? '.dev' : ''
        }.api.mitel.io/2017-09-01`;
    }

    getMicrosoftConnector( connectionId: string ): Promise<any>
    {
        return this.api.getIntegrationConnection( {integrationId: 'microsoft', connectionId: connectionId} ).then( result => {
            console.debug(`${logContext}.getMicrosoftConnector(${connectionId}) data=`, result);
            let data: any = {
                name: "",
                tenantId: "",
                clientId: "",
                clientSecret: "",
                connectionId: connectionId,
                connected: false
            }
            data.name = result.name;
            data.tenantId = result.authenticationSettings.properties.find((item) => item.name == 'tenant').value; 
            data.clientId = result.authenticationSettings.properties.find((item) => item.name == 'client_id').value; 
            data.clientSecret = result.authenticationSettings.properties.find((item) => item.name == 'client_secret').value; 
            data.connected = result.connected;

            return data;
        })
        .catch( err => {
            console.error(`${logContext}.getMicrosoftConnector(${connectionId}) error=`, err);
            throw err;
        })
    }

    saveMicrosoftConnector( connectionData: any ): Promise<any>
    {
        if ( connectionData.connectionId == undefined )
        {
            let params: IntegrationConnectionPostRequest = {
                name: connectionData.name,
                authenticationSettings: {
                    type: "http_oauth2_client_credentials",
                    properties: [
                        {
                            name: "tenant",
                            value: connectionData.tenantId,
                            secure: false
                        },
                        {
                            name: "client_id",
                            value: connectionData.clientId,
                            secure: false
                        },
                        {
                            name: "client_secret",
                            value: connectionData.clientSecret,
                            secure: false
                        },
                        {
                            name: "scope",
                            value: "https://graph.microsoft.com/.default",
                            secure: false
                        },
                    ]
                }
            }
            return this.api.createIntegrationConnection( {integrationId: 'microsoft', body: params }).then( result => {
                console.log(`${logContext}.saveMicrosoftConnectorthis is the microsoft integration connector`, result)
                return result;
            })
            .catch( err => {
                console.error(`${logContext}.saveMicrosoftConnector error creating the microsoft integration connector, err=`, err)
                throw(err);
            })
        }
        else
        {
            let params: IntegrationConnectionPutRequest = {
                name: connectionData.name,
                enabled: true,
                authenticationSettings: {
                    type: "http_oauth2_client_credentials",
                    properties: [
                        {
                            name: "tenant",
                            value: connectionData.tenantId,
                            secure: false
                        },
                        {
                            name: "client_id",
                            value: connectionData.clientId,
                            secure: false
                        },
                        {
                            name: "client_secret",
                            value: connectionData.clientSecret,
                            secure: false
                        },
                        {
                            name: "scope",
                            value: "https://graph.microsoft.com/.default",
                            secure: false
                        },
                    ]
                }
            }
            return this.api.updateIntegrationConnection( {
                integrationId: 'microsoft', 
                connectionId: connectionData.connectionId,
                body: params }).then( result => {
                console.log(`${logContext}.saveMicrosoftConnector this is the updated microsoft integration connector=`, result)
                return result;
            })
            .catch( err => {
                console.error(`${logContext}.saveMicrosoftConnector error updating the microsoft integration connector err=`, err)
                throw(err);
            })
        }
    }

    getMicrosoftAuthConnector( connectionId: string ): Promise<any>
    {
        return this.api.getIntegrationConnection( {integrationId: 'microsoft', connectionId: connectionId} ).then( result => {
            console.debug(`${logContext}.getMicrosoftAuthConnector(${connectionId}) data=`, result);
            return result;
        })
        .catch( err => {
            console.error(`${logContext}.getMicrosoftConnector(${connectionId}) error=`, err);
            throw err;
        })
    }

    saveMicrosoftAuthConnector( account: Account, connectionData: any ): Promise<any>
    {
        // looks like we can only pass a basic url? still investigating
        let theUrl = location.origin //+ "?accountId=" + account.accountId + "&context=MSTeamMonitorConsent"; //"&state=" + theState; 
        console.log("This is the url", theUrl);

        if ( connectionData.connectionId == undefined )
        {
            let params: IntegrationConnectionPostRequest = {
                name: "MSTeams To CloudLink Presence (" + connectionData.monitorId + ")",
                authenticationSettings: {
                    type: "http_oauth2_authorization_code",
                    properties: [
                        {
                            name: "tenant",
                            value: connectionData.tenantId,
                            secure: false
                        },
                        {
                            name: "client_id",
                            value: connectionData.clientId,
                            secure: false
                        },
                        {
                            name: "client_secret",
                            value: connectionData.clientSecret,
                            secure: false
                        },
                        {
                            name: "scope",
                            value: "openid email profile offline_access Presence.Read Presence.Read.All",
                            secure: false
                        },
                        {
                            name: "prompt",
                            value: "consent",
                            secure: false
                        },
                        {
                            name: "domain_hint",
                            value: "organizations",
                            secure: false
                        },
                    ]
                },
                clientRedirectUrl: theUrl
            }
            console.log("These are the params", params)
            return this.api.createIntegrationConnection( {integrationId: 'microsoft', body: params }).then( created => {
                console.log(`${logContext}.saveMicrosoftAuthConnector created microsoft integration connector`, created);
                return created;
            })
            .catch( err => {
                console.error(`${logContext}.saveMicrosoftAuthConnector error creating the microsoft integration connector err=`, err);
                throw(err);
            })
        }
        else // this is an update. For now it is on the client secret only. It seems to need all the params passed back
            // so we will request the connector, change the secret, set the url and update
        {
            return this.getMicrosoftAuthConnector( connectionData.connectionId ).then( existingConnector => {
                
                let params: IntegrationConnectionPutRequest = {
                    name: existingConnector.name,
                    enabled: existingConnector.enabled,
                    authenticationSettings: existingConnector.authenticationSettings,
                    clientRedirectUrl: theUrl
                }
                // update the client secret, tenant and client Id
                // usually it would be just a client secret update
                // rarely it is a tenant Id, and the client Id might be updated occassionaly if the parent ms graph connector changes
                // no harm in updating them all
                params.authenticationSettings.properties.find((item) => item.name == 'client_secret').value = connectionData.clientSecret;
                params.authenticationSettings.properties.find((item) => item.name == 'client_secret').secure = false;
                params.authenticationSettings.properties.find((item) => item.name == 'tenant').value = connectionData.tenantId;
                params.authenticationSettings.properties.find((item) => item.name == 'tenant').secure = false;
                params.authenticationSettings.properties.find((item) => item.name == 'client_id').value = connectionData.clientId;
                params.authenticationSettings.properties.find((item) => item.name == 'client_id').secure = false;

                return this.api.updateIntegrationConnection( {
                    integrationId: 'microsoft', 
                    connectionId: connectionData.connectionId,
                    body: params }).then( result => {
                    console.log(`${logContext}.saveMicrosoftAuthConnector updated microsoft integration connector`, result);
                    return result;
                })
                .catch( err => {
                    console.error(`${logContext}.saveMicrosoftAuthConnector error updating the microsoft integration connector err=`, err);
                    throw(err);
                });
            });
        }
    }

    // consider adding to the method above once you know more about refresh
    putMicrosoftAuthConnector( connectionData: any ): Promise<any>
    {
        let params: IntegrationConnectionPutRequest = {
            name: connectionData.name,
            enabled: connectionData.enabled,
            authenticationSettings: connectionData.authenticationSettings,
            clientRedirectUrl: location.origin  // consider a separate method if we can use something other than the basic url
        }

        return this.api.updateIntegrationConnection( {
            integrationId: 'microsoft', connectionId: connectionData.connectionId, body: params }).then(result => {
                return result;
        })
        .catch( err => {
            console.error(`${logContext}.saveMicrosoftAuthConnector error updating the microsoft integration connector err=`, err);
            throw(err);
        })
    }

    createMSTeamsPresenceWorkflow( connectionId: string, accountId: string ): Promise<any>
    {
        // Step 1: Request the MS Graph API integration data. If it no longer exists, throw an error
        return this.getMicrosoftConnector( connectionId ).then( connectorData => {
            if ( !connectorData || connectorData.clientId == null || connectorData.clientId.length == 0 )
            {
                // looks like the connector was removed outside the accounts app or there was an error
                // requesting it?
                let err: any = {
                    body: {
                        message: "missing MS teams integration"
                    }
                }
                throw err;
            }
            // Step 2: Create the workflow client
            return this.createWorkflowClient(accountId).then( data => {
                let client = data.client;
                let policyId = data.policyId;

                // Step 3: now create the options with the client token
                return this.authService.getClientAuthOptions(client).then( options => {
                    // Step 4: Clone the workflow
                    return this.cloneSpecifiedWorkflowAsClient( MSTeamsWorkflows.micollabPresenceToMSTeams.name,
                        MSTeamsWorkflows.micollabPresenceToMSTeams.flowId, options).then( workflow => {
                        // Step 5: Configure the workflow with the MS Connector
                        return this.updateMSTeamsPresenceWorkflowWithMSConnector( connectorData, workflow.id ).then(result => {
                            // Step 6: Apply client to the workflow
                            return this.applyClientToWorkflow( client, workflow.id, options).then( result => {
                                // Step 7: Enable the workflow
                                return this.enableWorkflow( workflow.id, workflow.version ).then( retVal => {                          
                                    // Step 8: Lock and hide the workflow
                                    return this.lockTheWorkflow( workflow.id ).then( retVal => {

                                        // looks like something is required afterall
                                        let req: WorkerPostRequest = {
                                            inputs: {
                                                "MSTeamsPresenceActivityId": workflow.id
                                            }
                                        }
                                        return this.executeSyncWorkflow(MSTeamsWorkflows.aggregatePresenceToMSTeams.flowId, req).then( result => {
                                            // we are done, collect information the caller might need and return it
                                            let retData: any = {
                                                clientId: client.clientId,
                                                policyId: policyId,
                                                workflowId: workflow.id,
                                                workflowVersion: workflow.version
                                            }
                                            return retData;
                                        })
                                    })
                                })
                            })
                        })
                    })
                })
            })
        })
        .catch (err => {
            if ( err && err.statusCode == 404 && err.body && err.body.message && err.body.message == connectionId )
            {
                // the MS teams connector is missing. Throw a specific error
                let newErr: any = {
                    body: {
                        message: "missing MS teams integration"
                    }
                }
                throw newErr;
            }
            console.error(`${logContext}.createMSTeamsPresenceWorkflow Creating the workflow failed during one of the steps. err=`, err);
            throw err;
        });

    }

    cloneSpecifiedWorkflowAsClient( workflowName: string, workflowId: string, options: any): Promise<any>
    {
        let params: ActivityPostRequest = {
            displayName: workflowName,
            cloneFromActivity: workflowId
        }
        return this.api.createActivity( {body: params, options: options} ).then(result => {
            console.log("Cloning the workflow success", result);
            return result;
        })
        .catch (err => {
            console.log("Cloning the wf failed", err);
            throw err;
        });
    }

    enableWorkflow( activityId: string, version: string ): Promise<any>
    {
        let params: ActivityPatchRequest = {
            operations: [
                {
                    op: "REPLACE",
                    path: "/active",
                    value: version
                }
            ]
        }
        return this.api.updateActivityAttributesById( {activityId: activityId, body: params} ).then(result => {
            console.log("updateActivityAttributesById to enable the workflowsuccess", activityId, version, result);
            return result;
        })
        .catch (err => {
            console.log("updateActivityAttributesById to enable the workflow failed", activityId, version, err);
            throw err;
        });
    }

    async deleteMSTeamsPresenceWorkflowAndAll(activityId: string, clientId: string, policyId: string, accountId: string): Promise<any>
    {
        // deleting is by best effort. If something fails we will not notify the user
        try {
            await this.deleteWorkflow( activityId);
            console.log("Removed the workflow success");
        }
        catch (e)
        {
            console.log("The deletion of the workflow failed, continuing with the rest of the deleting", e)
        }

        try {
            await this.adminService.deleteClientById( clientId );
            console.log("Removed the client Id success");
        }
        catch (e)
        {
            console.log("The deletion of the client failed, continuing with the rest of the deleting", e)
        }

        try {
            await this.adminService.deletePolicyById( accountId, policyId );
            console.log("Removed the client policy success");
        }
        catch (e)
        {
            console.log("The deletion of the client policy failed, continuing with the rest of the deleting", e)
        }
    }

    deleteWorkflow( workflowId: string ): Promise<any>
    {
        return this.api.deleteActivityById ( {activityId: workflowId} );
    }

    deleteMSTeamsConnector( connectionId: string ): Promise<any>
    {
        return this.api.deleteIntegrationConnection( {integrationId: 'microsoft', connectionId: connectionId }).then(result => {
            return;
        })
        .catch( err => {
            console.log("The deletion of the MS integration connection failed", err);
            return; // delete is best effort. No need to report to the user
        })
    }

    updateMSTeamsPresenceWorkflowWithMSConnector( connectorData: any, activityId: string ): Promise<any>
    {
        let params: ActivityPatchRequest = {
            operations:[
                {
                    op:"REPLACE",
                    path:"/variables/MicrosoftConnection",
                    value:{
                            valueType: "String",
                            description: null,
                            default: connectorData.connectionId,
                            isSecure: false,
                    }
                },
                {
                    op:"REPLACE",
                    path:"/variables/AppId",
                    value:{
                            valueType: "String",
                            description: null,
                            default: connectorData.clientId,
                            isSecure: false,

                    }
                }
            ]
        }
        console.log("Updating the MS Connector variable", activityId, params)
        return this.api.updateActivityAttributesById( {activityId: activityId, body: params} ).then(result => {
            console.log("updateActivityAttributesById for MS connector ", connectorData.connectionId, result);
            return ;
        })
        .catch (err => {
            console.log("updateActivityAttributesById  for MS connector failed", connectorData.connectionId, err);
            throw err;
        });  

    }

    updateMSTeamsPresenceWorkflowVariables( msGraphConnectorId: string, activityId: string, micollabWorkflowId: string ): Promise<any>
    {
        let params: ActivityPatchRequest = {
            operations:[
                {
                    op:"REPLACE",
                    path:"/variables/MicrosoftConnection",
                    value:{
                            valueType: "String",
                            description: null,
                            default: msGraphConnectorId,
                            isSecure: false,

                    }
                },
                {
                    op:"REPLACE",
                    path:"/variables/MiCollabUpdateFlowId",
                    value:{
                            valueType: "String",
                            description: null,
                            default: micollabWorkflowId,
                            isSecure: false,

                    }
                }
            ]
        }
        console.log("Updating the MS Connector variable", activityId, params)
        return this.api.updateActivityAttributesById( {activityId: activityId, body: params} ).then(result => {
            console.log("[WorkflowService].updateActivityAttributesById for MS connector ", msGraphConnectorId, result);
            return ;
        })
        .catch (err => {
            console.log("[WorkflowService].updateActivityAttributesById for MS connector failed", msGraphConnectorId, err);
            throw err;
        });  
    }

    updateMSTeamsPresenceWorkflowWithMSConnectorIdOnly( connectorData: any, activityId: string ): Promise<any>
    {
        let params: ActivityPatchRequest = {
            operations:[
                {
                    op:"REPLACE",
                    path:"/variables/MicrosoftConnection",
                    value:{
                            valueType: "String",
                            description: null,
                            default: connectorData.connectionId,
                            isSecure: false,

                    }
                }
            ]
        }
        console.log("Updating the MS Connector variable", activityId, params)
        return this.api.updateActivityAttributesById( {activityId: activityId, body: params} ).then(result => {
            console.log("[WorkflowService].updateActivityAttributesById for MS connector ", connectorData.connectionId, result);
            return ;
        })
        .catch (err => {
            console.log("[WorkflowService].updateActivityAttributesById for MS connector failed", connectorData.connectionId, err);
            throw err;
        });  
    }

    createWorkflowClient( accountId: string ): Promise<any>
    {
        // create the policy to restrict the workflow client user
        // for now we will just create a simple policy as part using some support portal policies
        return this.createWorkflowClientPolicy( accountId).then( policyId => {
            // create the client with the newly created policy
            return this.adminService.createAccountAdminClientWithPolicyId( policyId ).then( client => {
                return { client: client, policyId: policyId };
            })
            .catch (err => {
                console.log("createAccountAdminClientWithPolicyId for client failed", err);
                throw err;
            });
        })
        .catch (err => {
            console.log("createWorkflowClientPolicy for client failed", err);
            throw err;
        });
    }

    applyClientToWorkflow( client: Client, activityId: string, options: any): Promise<any>
    {
        // update the workflow to use the client
        let params: ActivityPatchRequest = {
            operations:[
                {
                    op:"REPLACE",
                    path:"/connectors",
                    value:{
                        cloudlink_identity:{
                        $type:"CloudlinkIdentity",
                        id: client.clientId
                        }
                    }
                }
            ]
        }
        return this.api.updateActivityAttributesById( {activityId: activityId, body: params, options: options} ).then(result => {
            console.log("updateActivityAttributesById for client", client.clientId, result);
            return result;
        })
        .catch (err => {
            console.log("updateActivityAttributesById for client failed", client.clientId, err);
            throw err;
        });
    }

    applyCertificateToWorkflow( activityId: string ): Promise<any>
    {
        // update the workflow to use a certificate
        let params: ActivityPatchRequest = {
            operations:[
                {
                    op:"REPLACE",
                    path:"/variables/Certificate",
                    value:{
                        valueType: "String",
                        description: null,
                        default: environment.msteams.certificate,
                        isSecure: true
                    }
                }
            ]
        }
        return this.api.updateActivityAttributesById( {activityId: activityId, body: params} ).then(result => {
            console.log("updateActivityAttributesById for certificate", activityId, result);
            return result;
        })
        .catch (err => {
            console.log("updateActivityAttributesById for certificate failed", activityId, err);
            throw err;
        });
    }

    applyScheduleToWorkflow( activityId: string ): Promise<any>
    {
        // update the workflow to use a schedule based on the time of day
        const time = new Date();
        let schedule = `DTSTART;TZID=Africa/Abidjan:20180101T${time.getHours().toString().padStart(2, '0')}${time.getMinutes().toString().padStart(2, '0')}${time.getSeconds().toString().padStart(2, '0')}\nRRULE:FREQ=MINUTELY;WKST=SU;INTERVAL=58`;
        let params: ActivityPatchRequest = {
            "operations": [
                {
                    "op": "REPLACE",
                    "path": "/trigger/inputs/Schedule/expression",
                    "value": schedule
                }
            ]
        };
        return this.api.updateActivityAttributesById( {activityId: activityId, body: params} ).then(result => {
            console.log("updateActivityAttributesById for schedule", activityId, result);
            return result;
        })
        .catch (err => {
            console.log("updateActivityAttributesById for schedule failed", activityId, err);
            throw err;
        });
    }

    applyPrivateKeyToWorkflow( activityId: string ): Promise<any>
    {
        // update the workflow to use a private key
        let params: ActivityPatchRequest = {
            operations:[
                {
                    op:"REPLACE",
                    path:"/variables/PrivateKey",
                    value:{
                        valueType: "String",
                        description: null,
                        default: environment.msteams.privateKey,
                        isSecure: true
                    }
                }
            ]
        }
        return this.api.updateActivityAttributesById( {activityId: activityId, body: params} ).then(result => {
            console.log("updateActivityAttributesById for private key", activityId, result);
            return result;
        })
        .catch (err => {
            console.log("updateActivityAttributesById for private key failed", activityId, err);
            throw err;
        });
    }

    createWorkflowClientPolicy( accountId: string ): Promise<string>
    {
        const policy: PolicyPostRequest = {
            name: accountId + '-workflow user policy for MS Teams Presence',
            statements: [
                DisableAllButUsersStatement
            ]
        };
        return this.adminService.createUserPolicy(policy, accountId).then( newPolicy => {
            return newPolicy.policyId;
        })
        .catch (err => {
            console.log("createUserPolicy for client failed", err);
            throw err;
        });
    }

    lockTheWorkflow( activityId: string ): Promise<any>
    {
        let params: ActivityPatchRequest = {
            operations: [
               {
                  op: "REPLACE",
                  path: "/lock",
                  value: true
               }
            ]
        } 

        return this.api.updateActivityAttributesById( {activityId: activityId, body: params} ).then(result => {
            console.log("updateActivityAttributesById for lock ", result);
            return result;
        })
        .catch (err => {
            console.log("updateActivityAttributesById for lock failed", err);
            throw err;
        });
    }

    saveTwilioConnector( connectionData: any ): Promise<any>
    {
        if ( connectionData.connectionId == undefined )
        {
            let params: IntegrationConnectionPostRequest = {
                name: connectionData.name,
                authenticationSettings: {
                    type: "http_basic",
                    properties: [
                        {
                            name: 'account_id',
                            value: connectionData.username,
                            secure: false
                        },
                        {
                            name: "username",
                            value: connectionData.username,
                            secure: false
                        },
                        {
                            name: "password",
                            value: connectionData.password,
                            secure: true 
                        }
                    ]
                }
            }
            return this.api.createIntegrationConnection( {integrationId: 'twilio', body: params }).then( result => {
                console.log("this is the twilio integration connector", result)
                return result;
            })
            .catch( err => {
                console.log("There was an error creating the twilio integration connector", err)
                throw(err);
            })
        }
    }

    saveCMcomConnector( connectionData: any ): Promise<any>
    {
        if ( connectionData.connectionId == undefined )
        {
            let params: IntegrationConnectionPostRequest = {
                name: connectionData.name,
                authenticationSettings: {
                    type: "http_apikey",
                    properties: [
                        {
                            name: "apikey",
                            value: connectionData.productToken,
                            secure: true
                        },
                        {
                            name: "account_id",
                            value: connectionData.cmcomAccountId,
                            secure: false
                        }
                    ]
                }
            }
            return this.api.createIntegrationConnection( {integrationId: 'cmcom', body: params }).then( result => {
                console.log("this is the cmcom integration connector", result)
                return result;
            })
            .catch( err => {
                console.log("There was an error creating the cmcom integration connector", err)
                throw(err);
            })
        }
    }

    getTag(tagId: string): Promise<Tag> {
        return this.api.getTagById({
            tagId: tagId
        })
    }

    createTag(tagId: string, description?: string, color?: string): Promise<TagCollection> {
        return this.api.createTags({
            body: {
                tags: [
                    {
                        tag: tagId,
                        description,
                        color
                    }
                ]
            }
        });
    }

    getActivityById(activityId: string) {
        return this.api.getActivityById({ activityId });
    }

    getActivitiesByTagId(tagId: string) {
        return this.api.getActivitiesByTag({ tagId });
    }

    async deleteActivitiesByTagId(tagId: string) {
     const flows = await this.api.getActivitiesByTag({tagId});
     if (flows.count > 0) {
        const items = flows._embedded.items;
        for (let i=0; i<items.length; i++) {
            await this.api.deleteActivityById({activityId: items[i].id});
        }
     }
    }

    deleteActivityById(activityId: string) {
        return this.api.deleteActivityById({ activityId })
            .catch((error) => {
                if (error.statusCode !== 404) throw error;
            });
    }

    cloneActivity(cloneFromActivity: string, displayName: string) {
        return this.api.createActivity({
            body: {
                cloneFromActivity,
                displayName
            }
        })
    }

    updateActivityAttributesUsingTokenProvided(accessToken: string, activityId: string, operations: ActivityPatchOperation[]) {
        return this.httpClient.patch(`${this.url}/activities/${activityId}`, {
            operations
        }, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            }
        }).toPromise();
    }

    updateActivityAttributes(activityId: string, operations: ActivityPatchOperation[]) {
        return this.api.updateActivityAttributesById({
            activityId,
            body: {
                operations
            }
        });
    }

    createClientAndClientOptions(accountId: string): Promise<any> {
        return this.createWorkflowClient(accountId).then( data => {
            // now create the options with the client token
            return this.authService.getClientAuthOptions(data.client).then( options => {
                return { client: data.client, policyId: data.policyId, options: options };
            });
        })
        .catch (err => {
            console.log("Creating the client and client token failed during one of the steps", err);
            throw err;
        });        
    }

    configureMSGraphWorkflowVariable( targetWorkflowId: string, msGraphWorkflowId: string ): Promise<any>
    {
        let params: ActivityPatchRequest = {
            operations:[
                {
                    op:"REPLACE",
                    path:"/variables/PresenceReceiverWorkerId",
                    value:{
                            valueType: "String",
                            description: null,
                            default: msGraphWorkflowId,
                            isSecure: false,
                    }
                }
            ]
        };

        return this.api.updateActivityAttributesById( {activityId: targetWorkflowId, body: params} ).then(result => {
            console.log("updateActivityAttributesById for PresenceReceiverWorkerId ", result);
            return result;
        })
        .catch (err => {
            console.log("updateActivityAttributesById for PresenceRecieverWorkerId failed", err);
            throw err;
        });
    }

    configureWorkflowConnectorIdVariable( workflowId: string, connectorIds: string[] ): Promise<any>
    {
        let params: ActivityPatchRequest = {
            operations: [
               {
                  op: "REPLACE",
                  path: "/variables/ConnectionIds",  
                  value: {
                        valueType: "Array",
                        description: null,
                        default: connectorIds,
                        isSecure: false
                    }
                }
            ]
        };

        return this.api.updateActivityAttributesById( {activityId: workflowId, body: params} ).then(result => {
            console.log("updateActivityAttributesById for ConnectionIds  ", result);
            return result;
        })
        .catch (err => {
            console.log("updateActivityAttributesById for ConnectionIds  failed", err);
            throw err;
        });
    }

    createMSGraphPresenceChangeWorkflow( msGraphConnectorId: string, client: Client, clientOptions: any, micollabPresenceWorkflowId: string ): Promise<any>
    {
        // Step 1: Clone the workflow
        return this.cloneSpecifiedWorkflowAsClient( MSTeamsWorkflows.msGraphPresenceChange.name,
            MSTeamsWorkflows.msGraphPresenceChange.flowId, clientOptions).then( workflow => {
            // Step 2: Configure the workflow with the MS Connector 
            return this.updateMSTeamsPresenceWorkflowVariables( msGraphConnectorId, workflow.id, micollabPresenceWorkflowId ).then(result => {
                // Step 3: Create a client and restricted policy and apply to the workflow
                return this.applyClientToWorkflow( client, workflow.id, clientOptions).then( result => {  
                    // Step 4:  apply private key to the workflow
                    return this.applyPrivateKeyToWorkflow( workflow.id ).then( retVal => {
                        // Step 5: disable the work flow 0.0.0 means disable   
                        return this.enableWorkflow( workflow.id, "0.0.0" ).then( result => {          
                            // Step 6: Lock and hide the workflow
                            return this.lockTheWorkflow( workflow.id ).then( retVal => {
                                // we are done, collect information the caller might need and return it
                                let retData: any = {
                                    workflowId: workflow.id,
                                    workflowVersion: workflow.version
                                }
                                return retData;
                            });
                        });
                    });
                })
            })
        })
        .catch (err => {
            console.error(`${logContext}.createMSGraphPresenceChangeWorkflow Creating the workflow failed during one of the steps err=`, err);
            throw err;
        });
    }

    createMSTeamsPresenceToPBXGenericWorkflow( 
        workflowConfig: any, 
        msGraphWorkflowId: string, 
        client: Client, 
        clientOptions: any ): Promise<any>
    {
        // Step 1: Clone the workflow
        return this.cloneSpecifiedWorkflowAsClient( workflowConfig.name, workflowConfig.flowId, clientOptions).then( workflow => {
            // Step 2:  apply client to the workflow
            return this.applyClientToWorkflow( client, workflow.id, clientOptions).then( result => {
                // Step 3: Configure the workflow with the MS Graph wf id
                return this.configureMSGraphWorkflowVariable( workflow.id, msGraphWorkflowId ).then( result => {      
                    // Step 4: disable the work flow 0.0.0 means disable   
                    return this.enableWorkflow( workflow.id, "0.0.0" ).then( result => {          
                        // Step 5: Lock and hide the workflow
                        return this.lockTheWorkflow( workflow.id ).then( retVal => {
                            // we are done, collect information the caller might need and return it
                            let retData: any = {
                                workflowId: workflow.id,
                                workflowVersion: workflow.version
                            }
                            return retData;
                        });
                    });
                })
            })
        })
    }

    createMSTeamsPresenceToPBXSchedulerWorkflow( 
        workflowConfig: any, 
        msGraphWorkflowId: string, 
        client: Client, 
        clientOptions: any ): Promise<any>
    {
        // Step 1: Clone the workflow
        return this.cloneSpecifiedWorkflowAsClient( workflowConfig.name, workflowConfig.flowId, clientOptions).then( workflow => {
            // Step 2:  apply client to the workflow
            return this.applyClientToWorkflow( client, workflow.id, clientOptions).then( result => {
                // Step 3: Configure the workflow with the MS Graph wf id
                return this.configureMSGraphWorkflowVariable( workflow.id, msGraphWorkflowId ).then( result => {    
                    // Step 4:  apply certificate to the workflow
                    return this.applyCertificateToWorkflow( workflow.id ).then( result => {
                        // Step 5: modify schedule input for Schedule Trigger based on time of day
                        return this.applyScheduleToWorkflow( workflow.id ).then ( result => {
                            // Step 6: disable the work flow 0.0.0 means disable   
                            return this.enableWorkflow( workflow.id, "0.0.0" ).then( result => {          
                                // Step 7: Lock and hide the workflow
                                return this.lockTheWorkflow( workflow.id ).then( result => {
                                    // we are done, collect information the caller might need and return it
                                    let retData: any = {
                                        workflowId: workflow.id,
                                        workflowVersion: workflow.version
                                    }
                                    return retData;
                                });
                            });
                        });
                    });
                });
            });
        });
    }

    createMiCollabPresenceStatusWorkflow( 
        client: Client, 
        clientOptions: any ): Promise<any>
    {
        // Step 1: Clone the workflow
        return this.cloneSpecifiedWorkflowAsClient( MSTeamsWorkflows.updateMiCollabPresenceStatus.name, MSTeamsWorkflows.updateMiCollabPresenceStatus.flowId, clientOptions).then( workflow => {
            // Step 2:  apply client to the workflow
            return this.applyClientToWorkflow( client, workflow.id, clientOptions).then( result => {
                // Step 3: Enable the workflow
                return this.enableWorkflow( workflow.id, "0.0.0" ).then( retVal => {                          
                    // Step 4: Lock and hide the workflow
                    return this.lockTheWorkflow( workflow.id ).then( retVal => {
                        // we are done, collect information the caller might need and return it
                        let retData: any = {
                            workflowId: workflow.id,
                            workflowVersion: workflow.version
                        }
                        return retData;
                    })
                })
            })
        })
    }

    createMSTeamsConnectorValidatorWorkflow( 
        client: Client, 
        clientOptions: any ): Promise<any>
    {
        // Step 1: Clone the workflow
        return this.cloneSpecifiedWorkflowAsClient( MSTeamsWorkflows.msTeamsPresenceToMitelPBXValidateConnections.name, 
            MSTeamsWorkflows.msTeamsPresenceToMitelPBXValidateConnections.flowId, clientOptions).then( workflow => {
            // Step 2:  apply client to the workflow
            return this.applyClientToWorkflow( client, workflow.id, clientOptions).then( result => {
                // Step 3:  apply certificate to the workflow
                return this.applyCertificateToWorkflow( workflow.id ).then( retVal => {
                    // Step 4: Enable the workflow
                    return this.enableWorkflow( workflow.id, workflow.version ).then( retVal => {                          
                        // Step 4: Lock and hide the workflow
                        return this.lockTheWorkflow( workflow.id ).then( retVal => {
                            // we are done, collect information the caller might need and return it
                            let retData: any = {
                                workflowId: workflow.id,
                                workflowVersion: workflow.version
                            }
                            return retData;
                        })
                    })
                })
            })
        })
    }

    updateMSGraphPresenceChangeWorkflowTimestamp( workflowId: string): Promise<any> 
    {
        let dateStr = new Date().toISOString();    
        let params: ActivityPatchRequest = {
            operations: [
               {
                  op: "REPLACE",
                  "path": "/variables/LastPresenceResetTimeStamp",
                  "value": {
                      "valueType": "String",
                      "description": null,
                      "default": dateStr,  // must be in ISO format"2024-03-18T07:15:46Z",
                      "isSecure": false
                  }
                }
            ]
        };

        return this.api.updateActivityAttributesById( {activityId: workflowId, body: params} ).then(result => {
            console.log(`${logContext}.updateMSGraphPresenceChangeWorkflowTimestamp updateActivityAttributesById for timestamp`, result);
            return result;
        })
        .catch (err => {
            console.error(`${logContext}.updateMSGraphPresenceChangeWorkflowTimestamp updateActivityAttributesById for timestamp failed`, err);
            throw err;
        });
    }
    
    async executeWorkflow( workflowId: string, reqBody: WorkerPostRequest ): Promise<any> 
    {
        await this.notifyService.initializeNotifyForWorkflow(workflowId);
        await Util.timeout(250); // wondering if the subscription is not fully created before execution?
        console.log(`${logContext}.executeWorkflow about to execute workflow ${workflowId} reqBody=`, reqBody);
        return this.api.runWorkerByActivityId( {activityId: workflowId, body: reqBody} ).then(result => {
            console.log(`${logContext}.executeWorkflow runWorkerByActivityId returned=`, result);
              if (result && result._links && result._links.self) {
                let workerId = result._links.self;
                const n = workerId.lastIndexOf('/');
                if (n > -1) {
                  workerId = workerId.slice(n + 1, workerId.length);
                  this.workerId = workerId;
                  this.workflowId = workflowId;
                  console.log(`${logContext}.executeWorkflow for workflow ${workflowId} workerId=${workerId}`);
                  if (this.workerId && this.notifyContent.workerId === workerId) {
                    console.log(`${logContext}.executeWorkflow - data is ready - return it`);

                    const outputs = this.notifyContent.data.arguments;
                    if ( Array.isArray(outputs) ) 
                    {
                        return outputs;
                    }
                    return [];
                  } else {
                    return [workerId];
                  }
                }
            }
            return [];
        })
    }

    // test for executing a WF and not waiting for a result
    async executeWorkflowWithNoWait( workflowId: string, reqBody: WorkerPostRequest ): Promise<any> 
    {
        console.log(`${logContext}.executeWorkflowWithNoWait about to execute workflow ${workflowId} reqBody=`, reqBody);
        return this.api.runWorkerByActivityId( {activityId: workflowId, body: reqBody} ).then(result => {
            console.log(`${logContext}.executeWorkflowWithNoWait runWorkerByActivityId returned=`, result);
                // we are going to keep the following part for now but it is not necessary
                // for debugging reasons only
                if (result && result._links && result._links.self) 
                {
                    let workerId = result._links.self;
                    const n = workerId.lastIndexOf('/');
                    if (n > -1) {
                    workerId = workerId.slice(n + 1, workerId.length);
                    console.log(`${logContext}.executeWorkflowWithNoWait for workflow ${workflowId} workerId=${workerId}`);
                    const params = { activityId: workflowId, workerId: workerId };

                    return this.api.getWorkerByActivityIdAndWorkerId(params).then(testExec => {
                        console.log(`${logContext}.executeWorkflowWithNoWait() getWorkerByActivityIdAndWorkerId result=`, testExec);
                        return;
                    })
                }
                return;
            }
            return;
        })
    }

    async executeSyncWorkflow(workflowId: string, reqBody: WorkerPostRequest): Promise<any> {
        console.log(`${logContext}.executeSyncWorkflow about to execute workflow ${workflowId} reqBody=`, reqBody);
        return this.api.runWorkerByActivityId({ activityId: workflowId, body: reqBody }).then(result => {
            console.log(`${logContext}.executeSyncWorkflow runWorkerByActivityId returned=`, result);
            return result; 
        })
    }

    // in the case of a timeout or a failure
    // clear the subscription
    clearWorkflowSubscriptionInProgress()
    {
        this.notifyService.unsubscribeFromWorkflow();
        this.workerId = '';
        this.workflowId = '';
        this.notifyContent = {};
    }

    runWorkerByActivityId( activityId: string, reqBody: WorkerPostRequest ): Promise<any> {
        return this.api.runWorkerByActivityId( {activityId: activityId, body: reqBody} );
    }
}
